import React, { useState, useEffect } from 'react';
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    IconButton,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
    Switch,
    FormControlLabel,
    Grid,
    List,
    ListItem,
    ListItemText,
    ListItemSecondaryAction,
    Alert,
    Snackbar,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Divider,
} from '@mui/material';
import {
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    Close as CloseIcon,
} from '@mui/icons-material';
import {
    getAllCustomers,
    getCustomer,
    createCustomer,
    updateCustomer,
    deleteCustomer,
    addTeamToCustomer,
    removeTeamFromCustomer,
} from '../../services/customerService';
import { getAllTeams } from '../../services/teamService';

const Customers = () => {
    const [customers, setCustomers] = useState([]);
    const [selectedCustomer, setSelectedCustomer] = useState(null);
    const [isFormOpen, setIsFormOpen] = useState(false);
    const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
    const [error, setError] = useState(null);
    const [sortConfig, setSortConfig] = useState({
        direction: 'asc',
        field: 'name'
    });
    const [availableTeams, setAvailableTeams] = useState([]);
    const [selectedTeam, setSelectedTeam] = useState('');
    const [formData, setFormData] = useState({
        name: '',
        accountNumber: '',
        companyName: '',
        address: '',
        email: '',
        website: '',
        phone: '',
        fax: '',
        billable: true,
    });

    useEffect(() => {
        fetchCustomers();
        fetchTeams();
    }, []);

    const fetchTeams = async () => {
        try {
            const teams = await getAllTeams();
            const sortedTeams = teams.sort((a, b) => a.name.localeCompare(b.name));
            setAvailableTeams(sortedTeams);
        } catch (error) {
            console.error('Error fetching teams:', error);
            setError(error.message);
        }
    };

    const fetchCustomers = async () => {
        try {
            const data = await getAllCustomers();
            setCustomers(data);
            setError(null);
        } catch (error) {
            console.error('Error fetching customers:', error);
            setError(error.message);
            if (error.message.includes('authentication') || error.message.includes('token')) {
                window.location.href = '/login';
            }
        }
    };

    const refreshCustomerData = async (customerId) => {
        try {
            const updatedCustomer = await getCustomer(customerId);
            setSelectedCustomer(updatedCustomer);
            return updatedCustomer;
        } catch (error) {
            console.error('Error refreshing customer data:', error);
            setError(error.message);
            return null;
        }
    };

    const handleFormOpen = async (customer = null) => {
        if (customer) {
            try {
                const updatedCustomer = await getCustomer(customer._id);
                setFormData({
                    name: updatedCustomer.name,
                    accountNumber: updatedCustomer.accountNumber || '',
                    companyName: updatedCustomer.companyName || '',
                    address: updatedCustomer.address || '',
                    email: updatedCustomer.email || '',
                    website: updatedCustomer.website || '',
                    phone: updatedCustomer.phone || '',
                    fax: updatedCustomer.fax || '',
                    billable: updatedCustomer.billable,
                });
                setSelectedCustomer(updatedCustomer);
            } catch (error) {
                console.error('Error fetching customer details:', error);
                setError(error.message);
            }
        } else {
            setFormData({
                name: '',
                accountNumber: '',
                companyName: '',
                address: '',
                email: '',
                website: '',
                phone: '',
                fax: '',
                billable: true,
            });
            setSelectedCustomer(null);
        }
        setIsFormOpen(true);
    };

    const handleFormClose = () => {
        setIsFormOpen(false);
        setSelectedCustomer(null);
        setFormData({
            name: '',
            accountNumber: '',
            companyName: '',
            address: '',
            email: '',
            website: '',
            phone: '',
            fax: '',
            billable: true,
        });
        setError(null);
        setSelectedTeam('');
    };

    const handleInputChange = (e) => {
        const { name, value, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: name === 'billable' ? checked : value,
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            if (selectedCustomer) {
                await updateCustomer(selectedCustomer._id, formData);
            } else {
                await createCustomer(formData);
            }
            handleFormClose();
            fetchCustomers();
            setError(null);
        } catch (error) {
            console.error('Error saving customer:', error);
            setError(error.message);
            if (error.message.includes('authentication') || error.message.includes('token')) {
                window.location.href = '/login';
            }
        }
    };

    const handleDeleteClick = (customer) => {
        setSelectedCustomer(customer);
        setIsDeleteDialogOpen(true);
    };

    const handleDeleteConfirm = async () => {
        try {
            await deleteCustomer(selectedCustomer._id);
            setIsDeleteDialogOpen(false);
            setSelectedCustomer(null);
            fetchCustomers();
            setError(null);
        } catch (error) {
            console.error('Error deleting customer:', error);
            setError(error.message);
            if (error.message.includes('authentication') || error.message.includes('token')) {
                window.location.href = '/login';
            }
        }
    };

    const handleAddTeam = async () => {
        if (!selectedTeam) return;
        try {
            await addTeamToCustomer(selectedCustomer._id, selectedTeam);
            await refreshCustomerData(selectedCustomer._id);
            setSelectedTeam('');
            setError(null);
        } catch (error) {
            console.error('Error adding team:', error);
            setError(error.message);
        }
    };

    const handleRemoveTeam = async (teamId) => {
        try {
            await removeTeamFromCustomer(selectedCustomer._id, teamId);
            await refreshCustomerData(selectedCustomer._id);
            setError(null);
        } catch (error) {
            console.error('Error removing team:', error);
            setError(error.message);
        }
    };

    const handleErrorClose = () => {
        setError(null);
    };

    return (
        <Box sx={{ 
            p: 3,
            '@media (max-width:600px)': {
                p: 1, // Much more reduced padding for mobile
                paddingTop: 0, // No top padding - layout provides spacing
                paddingBottom: '88px', // Account for bottom navigation
            },
            '@media (max-width:900px) and (orientation: landscape)': {
                p: 0.5, // Minimal padding in landscape mode
                paddingTop: 0, // No top padding
                paddingBottom: 0.5, // Minimal bottom padding
                height: 'auto', // Allow natural height
                overflow: 'visible', // Allow content to overflow
                minHeight: 'auto', // Remove height constraints
            }
        }}>
            <Snackbar 
                open={!!error} 
                autoHideDuration={6000} 
                onClose={handleErrorClose}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <Alert onClose={handleErrorClose} severity="error" sx={{ width: '100%' }}>
                    {error}
                </Alert>
            </Snackbar>

            <Box sx={{ 
                display: 'flex', 
                justifyContent: { xs: 'center', sm: 'flex-end' }, 
                mb: 3 
            }}>
                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => handleFormOpen()}
                    sx={{
                        '@media (max-width:600px)': {
                            fontSize: '0.875rem',
                            padding: '8px 16px',
                        }
                    }}
                >
                    Add Customer
                </Button>
            </Box>

            <TableContainer 
                component={Paper}
                sx={{
                    overflow: 'auto',
                    '& .MuiTable-root': {
                        borderCollapse: 'separate',
                        borderSpacing: 0
                    }
                }}
            >
                <Table 
                    size="small" 
                    stickyHeader
                    sx={{
                        '@media (min-width:901px)': {
                            '& .MuiTableCell-root': {
                                padding: '16px',
                            }
                        }
                    }}
                >
                    <TableHead sx={{ bgcolor: '#e0e0e0' }}>
                        <TableRow>
                            <TableCell 
                                onClick={() => {
                                    setSortConfig(prev => ({
                                        direction: prev.field === 'name' && prev.direction === 'asc' ? 'desc' : 'asc',
                                        field: 'name'
                                    }));
                                }}
                                sx={{ 
                                    cursor: 'pointer',
                                    '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' },
                                    userSelect: 'none',
                                    '@media (max-width:900px)': {
                                        padding: '8px',
                                        fontSize: '0.75rem',
                                    }
                                }}
                            >
                                Customer Name
                                {sortConfig.field === 'name' && (
                                    <span style={{ marginLeft: '4px' }}>
                                        {sortConfig.direction === 'asc' ? '▲' : '▼'}
                                    </span>
                                )}
                            </TableCell>
                            <TableCell 
                                align="center"
                                sx={{
                                    '@media (max-width:900px)': {
                                        padding: '8px',
                                        fontSize: '0.75rem',
                                    }
                                }}
                            >
                                Teams Count
                            </TableCell>
                            <TableCell 
                                align="right"
                                sx={{
                                    '@media (max-width:900px)': {
                                        padding: '8px',
                                        fontSize: '0.75rem',
                                    }
                                }}
                            >
                                Actions
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {[...customers]
                            .sort((a, b) => {
                                if (sortConfig.field === 'name') {
                                    return sortConfig.direction === 'asc'
                                        ? a.name.localeCompare(b.name)
                                        : b.name.localeCompare(a.name);
                                }
                                return 0;
                            })
                            .map((customer) => (
                            <TableRow key={customer._id}>
                                <TableCell sx={{
                                    '@media (max-width:900px)': {
                                        padding: '8px',
                                        fontSize: '0.75rem',
                                    }
                                }}>
                                    {customer.name}
                                </TableCell>
                                <TableCell 
                                    align="center"
                                    sx={{
                                        '@media (max-width:900px)': {
                                            padding: '8px',
                                            fontSize: '0.75rem',
                                        }
                                    }}
                                >
                                    {customer.teams?.length || 0}
                                </TableCell>
                                <TableCell 
                                    align="right"
                                    sx={{
                                        '@media (max-width:900px)': {
                                            padding: '8px',
                                            fontSize: '0.75rem',
                                        }
                                    }}
                                >
                                    <IconButton
                                        onClick={() => handleFormOpen(customer)}
                                        size="small"
                                        sx={{
                                            '@media (max-width:900px)': {
                                                padding: '4px',
                                                '& .MuiSvgIcon-root': {
                                                    fontSize: '1rem',
                                                }
                                            }
                                        }}
                                    >
                                        <EditIcon />
                                    </IconButton>
                                    <IconButton
                                        onClick={() => handleDeleteClick(customer)}
                                        size="small"
                                        sx={{
                                            '@media (max-width:900px)': {
                                                padding: '4px',
                                                '& .MuiSvgIcon-root': {
                                                    fontSize: '1rem',
                                                }
                                            }
                                        }}
                                    >
                                        <DeleteIcon />
                                    </IconButton>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* Customer Form Dialog */}
            <Dialog
                open={isFormOpen}
                onClose={handleFormClose}
                maxWidth="md"
                fullWidth
                sx={{
                    '& .MuiDialog-paper': {
                        bgcolor: 'background.paper',
                        maxHeight: '90vh',
                        display: 'flex',
                        flexDirection: 'column'
                    },
                    '& .MuiBackdrop-root': {
                        backgroundColor: 'rgba(0, 0, 0, 0.7)',
                    },
                }}
            >
                <DialogTitle sx={{
                    '@media (max-width:600px)': {
                        fontSize: '1.1rem',
                        padding: '16px',
                        paddingBottom: '8px',
                    }
                }}>
                    {selectedCustomer ? 'Edit Customer' : 'Create Customer'}
                </DialogTitle>
                <DialogContent sx={{ 
                    flex: 1, 
                    overflow: 'auto',
                    paddingTop: '24px !important',
                    '@media (max-width:600px)': {
                        padding: '16px',
                        paddingTop: '24px !important',
                    }
                }}>
                    <form id="customer-form" onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    name="name"
                                    label="Name"
                                    value={formData.name}
                                    onChange={handleInputChange}
                                    fullWidth
                                    required
                                    margin="none"
                                    sx={{
                                        '@media (max-width:600px)': {
                                            marginBottom: '8px',
                                            '& .MuiInputBase-root': {
                                                fontSize: '0.875rem',
                                                height: '40px',
                                            },
                                            '& .MuiInputLabel-root': {
                                                fontSize: '0.875rem',
                                            },
                                            '& .MuiOutlinedInput-input': {
                                                padding: '8px 14px',
                                            }
                                        }
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    name="accountNumber"
                                    label="Account Number"
                                    value={formData.accountNumber}
                                    onChange={handleInputChange}
                                    fullWidth
                                    margin="none"
                                    sx={{
                                        '@media (max-width:600px)': {
                                            marginBottom: '8px',
                                            '& .MuiInputBase-root': {
                                                fontSize: '0.875rem',
                                                height: '40px',
                                            },
                                            '& .MuiInputLabel-root': {
                                                fontSize: '0.875rem',
                                            },
                                            '& .MuiOutlinedInput-input': {
                                                padding: '8px 14px',
                                            }
                                        }
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    name="companyName"
                                    label="Company Name"
                                    value={formData.companyName}
                                    onChange={handleInputChange}
                                    fullWidth
                                    margin="none"
                                    sx={{
                                        '@media (max-width:600px)': {
                                            marginBottom: '8px',
                                            '& .MuiInputBase-root': {
                                                fontSize: '0.875rem',
                                                height: '40px',
                                            },
                                            '& .MuiInputLabel-root': {
                                                fontSize: '0.875rem',
                                            },
                                            '& .MuiOutlinedInput-input': {
                                                padding: '8px 14px',
                                            }
                                        }
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    name="address"
                                    label="Address"
                                    value={formData.address}
                                    onChange={handleInputChange}
                                    fullWidth
                                    multiline
                                    rows={2}
                                    margin="none"
                                    sx={{
                                        '@media (max-width:600px)': {
                                            marginBottom: '8px',
                                            '& .MuiInputBase-root': {
                                                fontSize: '0.875rem',
                                            },
                                            '& .MuiInputLabel-root': {
                                                fontSize: '0.875rem',
                                            }
                                        }
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    name="email"
                                    label="Email"
                                    type="email"
                                    value={formData.email}
                                    onChange={handleInputChange}
                                    fullWidth
                                    margin="none"
                                    sx={{
                                        '@media (max-width:600px)': {
                                            marginBottom: '8px',
                                            '& .MuiInputBase-root': {
                                                fontSize: '0.875rem',
                                                height: '40px',
                                            },
                                            '& .MuiInputLabel-root': {
                                                fontSize: '0.875rem',
                                            },
                                            '& .MuiOutlinedInput-input': {
                                                padding: '8px 14px',
                                            }
                                        }
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    name="website"
                                    label="Website"
                                    value={formData.website}
                                    onChange={handleInputChange}
                                    fullWidth
                                    margin="none"
                                    sx={{
                                        '@media (max-width:600px)': {
                                            marginBottom: '8px',
                                            '& .MuiInputBase-root': {
                                                fontSize: '0.875rem',
                                                height: '40px',
                                            },
                                            '& .MuiInputLabel-root': {
                                                fontSize: '0.875rem',
                                            },
                                            '& .MuiOutlinedInput-input': {
                                                padding: '8px 14px',
                                            }
                                        }
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    name="phone"
                                    label="Phone"
                                    value={formData.phone}
                                    onChange={handleInputChange}
                                    fullWidth
                                    margin="none"
                                    sx={{
                                        '@media (max-width:600px)': {
                                            marginBottom: '8px',
                                            '& .MuiInputBase-root': {
                                                fontSize: '0.875rem',
                                                height: '40px',
                                            },
                                            '& .MuiInputLabel-root': {
                                                fontSize: '0.875rem',
                                            },
                                            '& .MuiOutlinedInput-input': {
                                                padding: '8px 14px',
                                            }
                                        }
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    name="fax"
                                    label="Fax"
                                    value={formData.fax}
                                    onChange={handleInputChange}
                                    fullWidth
                                    margin="none"
                                    sx={{
                                        '@media (max-width:600px)': {
                                            marginBottom: '8px',
                                            '& .MuiInputBase-root': {
                                                fontSize: '0.875rem',
                                                height: '40px',
                                            },
                                            '& .MuiInputLabel-root': {
                                                fontSize: '0.875rem',
                                            },
                                            '& .MuiOutlinedInput-input': {
                                                padding: '8px 14px',
                                            }
                                        }
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            name="billable"
                                            checked={formData.billable}
                                            onChange={handleInputChange}
                                            color="primary"
                                        />
                                    }
                                    label="Billable"
                                    sx={{
                                        '@media (max-width:600px)': {
                                            '& .MuiFormControlLabel-label': {
                                                fontSize: '0.875rem',
                                            }
                                        }
                                    }}
                                />
                            </Grid>
                        </Grid>

                        {selectedCustomer && (
                            <>
                                <Box sx={{ mt: 3, mb: 2 }}>
                                    <Typography variant="h6" sx={{ mb: 2 }}>
                                        Assigned Teams
                                    </Typography>
                                    <Grid container spacing={2} alignItems="center">
                                        <Grid item xs>
                                            <FormControl fullWidth>
                                                <InputLabel>Select Team</InputLabel>
                                                <Select
                                                    value={selectedTeam}
                                                    onChange={(e) => setSelectedTeam(e.target.value)}
                                                    label="Select Team"
                                                >
                                                    {availableTeams.map((team) => (
                                                        <MenuItem key={team._id} value={team._id}>
                                                            {team.name}
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                        </Grid>
                                        <Grid item>
                                            <Button
                                                variant="contained"
                                                onClick={handleAddTeam}
                                                disabled={!selectedTeam}
                                            >
                                                Add Team
                                            </Button>
                                        </Grid>
                                    </Grid>
                                    <List sx={{ mt: 2, maxHeight: 200, overflow: 'auto' }}>
                                        {selectedCustomer.teams?.map((team, index) => (
                                            <React.Fragment key={team._id}>
                                                <ListItem>
                                                    <ListItemText primary={team.name} />
                                                    <ListItemSecondaryAction>
                                                        <IconButton
                                                            edge="end"
                                                            onClick={() => handleRemoveTeam(team._id)}
                                                        >
                                                            <DeleteIcon />
                                                        </IconButton>
                                                    </ListItemSecondaryAction>
                                                </ListItem>
                                                {index < selectedCustomer.teams.length - 1 && (
                                                    <Divider component="li" />
                                                )}
                                            </React.Fragment>
                                        ))}
                                    </List>
                                </Box>

                            </>
                        )}
                    </form>
                </DialogContent>
                <DialogActions sx={{ p: 3, pt: 0, justifyContent: 'flex-end' }}>
                    <Button onClick={handleFormClose}>Cancel</Button>
                    <Button type="submit" form="customer-form" variant="contained" color="primary">
                        {selectedCustomer ? 'Update' : 'Create'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <Dialog
                open={isDeleteDialogOpen}
                onClose={() => setIsDeleteDialogOpen(false)}
            >
                <DialogTitle>Confirm Delete</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Are you sure you want to delete {selectedCustomer?.name}? This action cannot be undone.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setIsDeleteDialogOpen(false)}>Cancel</Button>
                    <Button onClick={handleDeleteConfirm} color="error" variant="contained">
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default Customers;
